import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  GET_STAFFS, CREATE_STAFF, UPDATE_STAFF, DELETE_STAFF,
  GENERATE_STAFF_LOGIN_ID, RESET_STAFF_PASSWORD,
} from '../graphql/queries';

const LIMIT = 10;

const initialForm = { name: '', email: '', phone: '' };

// ── Created Credentials Dialog ────────────────────────────────
function CredentialsDialog({ loginId, password, onClose }) {
  const [copied, setCopied] = useState(false);

  const copyAll = () => {
    navigator.clipboard.writeText(`Login ID: ${loginId}\nPassword: ${password}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal--sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width={20} height={20} fill="none" viewBox="0 0 24 24" stroke="#16a34a" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="modal-title" style={{ margin: 0 }}>Staff Account Created</h2>
          </div>
          <button className="modal-close" onClick={onClose}>
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="modal-body">
          <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 20 }}>
            Save these credentials — the password cannot be retrieved later.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Login ID
              </span>
              <code style={{
                display: 'block', fontSize: '1.05rem', fontWeight: 700, color: '#1e293b',
                background: '#f1f5f9', padding: '10px 14px', borderRadius: 8,
                letterSpacing: '0.1em', border: '1px solid #e2e8f0',
              }}>
                {loginId}
              </code>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Password
              </span>
              <code style={{
                display: 'block', fontSize: '1.05rem', fontWeight: 700, color: '#1e293b',
                background: '#f1f5f9', padding: '10px 14px', borderRadius: 8,
                letterSpacing: '0.1em', border: '1px solid #e2e8f0',
              }}>
                {password}
              </code>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn--ghost" onClick={onClose}>Close</button>
          <button className="btn btn--primary" onClick={copyAll}>
            {copied ? '✓ Copied!' : 'Copy credentials'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Edit Staff Modal ───────────────────────────────────────────
function StaffModal({ mode, form, onChange, onSubmit, onClose, loading }) {
  const isEdit = mode === 'edit';
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{isEdit ? 'Edit Staff' : 'Add Staff'}</h2>
          <button className="modal-close" onClick={onClose}>
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form className="modal-body" onSubmit={onSubmit}>
          <div className="form-group">
            <label className="form-label">Name <span className="form-required">*</span></label>
            <input
              className="form-input"
              placeholder="Full name"
              value={form.name}
              onChange={(e) => onChange('name', e.target.value)}
              required
            />
          </div>
          {!isEdit && (
            <div className="form-group">
              <label className="form-label">Email <span className="form-required">*</span></label>
              <input
                className="form-input"
                type="email"
                placeholder="email@example.com"
                value={form.email}
                onChange={(e) => onChange('email', e.target.value)}
                required
              />
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input
              className="form-input"
              placeholder="+1 000 000 0000"
              value={form.phone}
              onChange={(e) => onChange('phone', e.target.value)}
            />
          </div>
          {!isEdit && (
            <p className="form-hint" style={{ fontSize: '0.82rem', color: '#64748b', marginTop: '0.25rem' }}>
              A Login ID and password will be auto-generated after creation.
            </p>
          )}
          <div className="modal-footer">
            <button type="button" className="btn btn--ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn--primary" disabled={loading}>
              {loading ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Staff'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Delete Confirm ─────────────────────────────────────────────
function DeleteModal({ staff, onConfirm, onClose, loading }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal--sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Delete Staff</h2>
          <button className="modal-close" onClick={onClose}>
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="modal-body">
          <p className="delete-confirm-text">
            Are you sure you want to remove <strong>{staff.name}</strong>? This action cannot be undone.
          </p>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn--ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn--danger" onClick={onConfirm} disabled={loading}>
            {loading ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Generate Login ID Confirm ──────────────────────────────────
function GenerateLoginIdModal({ staff, onConfirm, onClose, loading }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal--sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Generate Login ID</h2>
          <button className="modal-close" onClick={onClose}>
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="modal-body">
          <p className="delete-confirm-text">
            Generate a Login ID for <strong>{staff.name}</strong>? The ID will be based on your store prefix and cannot be changed once set.
          </p>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn--ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn--primary" onClick={onConfirm} disabled={loading}>
            {loading ? 'Generating...' : 'Generate'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Reset Password Confirm ─────────────────────────────────────
function ResetPasswordModal({ staff, onConfirm, onClose, loading }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal--sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Reset Password</h2>
          <button className="modal-close" onClick={onClose}>
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="modal-body">
          <p className="delete-confirm-text">
            Reset the password for <strong>{staff.name}</strong>? A new password will be auto-generated and sent to <strong>{staff.email}</strong>.
          </p>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn--ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn--danger" onClick={onConfirm} disabled={loading}>
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Pagination ─────────────────────────────────────────────────
function Pagination({ page, totalPages, onPrev, onNext }) {
  if (totalPages <= 1) return null;
  return (
    <div className="pagination">
      <button className="pagination-btn" onClick={onPrev} disabled={page <= 1}>
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Prev
      </button>
      <span className="pagination-info">Page {page} of {totalPages}</span>
      <button className="pagination-btn" onClick={onNext} disabled={page >= totalPages}>
        Next
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────
export default function Staff() {
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [toast, setToast] = useState(null);
  const [createdCredentials, setCreatedCredentials] = useState(null);

  const { data, loading: listLoading, refetch } = useQuery(GET_STAFFS, {
    variables: { page, limit: LIMIT },
    fetchPolicy: 'network-only',
  });

  const [createStaff, { loading: creating }] = useMutation(CREATE_STAFF);
  const [updateStaff, { loading: updating }] = useMutation(UPDATE_STAFF);
  const [deleteStaff, { loading: deleting }] = useMutation(DELETE_STAFF);
  const [generateLoginId, { loading: generating }] = useMutation(GENERATE_STAFF_LOGIN_ID);
  const [resetPassword, { loading: resetting }] = useMutation(RESET_STAFF_PASSWORD);

  const staffData = data?.getStaffs;
  const staffs = staffData?.staffs || [];
  const totalPages = staffData?.totalPages || 1;
  const total = staffData?.total || 0;

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const openCreate = () => {
    setForm(initialForm);
    setModal('create');
  };

  const openEdit = (staff) => {
    setSelected(staff);
    setForm({ name: staff.name, phone: staff.phone || '' });
    setModal('edit');
  };

  const openDelete = (staff) => {
    setSelected(staff);
    setModal('delete');
  };

  const openGenerateLoginId = (staff) => {
    setSelected(staff);
    setModal('generate-login-id');
  };

  const openResetPassword = (staff) => {
    setSelected(staff);
    setModal('reset-password');
  };

  const closeModal = () => {
    setModal(null);
    setSelected(null);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const { name, email, phone } = form;
    const { data: res } = await createStaff({
      variables: { name, email, phone: phone || undefined },
    });
    if (res?.createStaff?.error) {
      showToast(res.createStaff.message, 'error');
    } else {
      closeModal();
      refetch();
      const { loginId, generatedPassword } = res.createStaff;
      if (loginId && generatedPassword) {
        setCreatedCredentials({ loginId, password: generatedPassword });
      } else {
        showToast('Staff member added successfully');
      }
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const { name, phone } = form;
    const { data: res } = await updateStaff({
      variables: { id: selected.id, name, phone: phone || undefined },
    });
    if (res?.updateStaff?.error) {
      showToast(res.updateStaff.message, 'error');
    } else {
      showToast('Staff member updated successfully');
      closeModal();
      refetch();
    }
  };

  const handleDelete = async () => {
    const { data: res } = await deleteStaff({ variables: { id: selected.id } });
    if (res?.deleteStaff?.error) {
      showToast(res.deleteStaff.message, 'error');
    } else {
      showToast('Staff member removed');
      closeModal();
      if (staffs.length === 1 && page > 1) setPage((p) => p - 1);
      else refetch();
    }
  };

  const handleGenerateLoginId = async () => {
    const { data: res } = await generateLoginId({ variables: { id: selected.id } });
    if (res?.generateStaffLoginId?.error) {
      showToast(res.generateStaffLoginId.message, 'error');
    } else {
      showToast(`Login ID generated: ${res.generateStaffLoginId.loginId}`);
      closeModal();
      refetch();
    }
  };

  const handleResetPassword = async () => {
    const { data: res } = await resetPassword({ variables: { id: selected.id } });
    if (res?.resetStaffPassword?.error) {
      showToast(res.resetStaffPassword.message, 'error');
    } else {
      showToast('Password reset. New credentials sent to staff email.');
      closeModal();
    }
  };

  return (
    <div className="staff-page">
      {toast && (
        <div className={`toast toast--${toast.type}`}>{toast.message}</div>
      )}

      <header className="dashboard-header">
        <div>
          <h1 className="dashboard-title">Staff</h1>
          <p className="dashboard-subtitle">
            {listLoading ? 'Loading...' : `${total} member${total !== 1 ? 's' : ''} in your store`}
          </p>
        </div>
        <button className="btn btn--primary" onClick={openCreate}>
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Staff
        </button>
      </header>

      <main className="dashboard-main">
        <div className="staff-card">
          {listLoading ? (
            <div className="staff-loading">
              <div className="loader-ring" />
            </div>
          ) : staffs.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <p className="empty-state-title">No staff members yet</p>
              <p className="empty-state-desc">Add your first staff member to get started.</p>
              <button className="btn btn--primary" onClick={openCreate}>Add Staff</button>
            </div>
          ) : (
            <>
              <div className="staff-table-wrap">
                <table className="staff-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Login ID</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staffs.map((s) => (
                      <tr key={s.id}>
                        <td>
                          <div className="staff-name-cell">
                            <div className="staff-avatar">{s.name.charAt(0).toUpperCase()}</div>
                            <span className="staff-name">{s.name}</span>
                          </div>
                        </td>
                        <td>
                          {s.loginId ? (
                            <code style={{ fontSize: '0.82rem', background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>
                              {s.loginId}
                            </code>
                          ) : (
                            <button
                              className="btn btn--ghost"
                              style={{ fontSize: '0.78rem', padding: '3px 10px', height: 'auto' }}
                              onClick={() => openGenerateLoginId(s)}
                              title="Generate Login ID"
                            >
                              Generate ID
                            </button>
                          )}
                        </td>
                        <td className="staff-email">{s.email}</td>
                        <td className="staff-phone">{s.phone || <span className="text-placeholder">—</span>}</td>
                        <td>
                          <div className="row-actions">
                            <button className="row-btn row-btn--edit" onClick={() => openEdit(s)} title="Edit">
                              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button className="row-btn row-btn--edit" onClick={() => openResetPassword(s)} title="Reset Password" style={{ color: '#f59e0b' }}>
                              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                              </svg>
                            </button>
                            <button className="row-btn row-btn--delete" onClick={() => openDelete(s)} title="Delete">
                              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="staff-table-footer">
                <span className="pagination-count">
                  Showing {staffs.length} of {total}
                </span>
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  onPrev={() => setPage((p) => p - 1)}
                  onNext={() => setPage((p) => p + 1)}
                />
              </div>
            </>
          )}
        </div>
      </main>

      {(modal === 'create' || modal === 'edit') && (
        <StaffModal
          mode={modal}
          form={form}
          onChange={handleFormChange}
          onSubmit={modal === 'create' ? handleCreate : handleUpdate}
          onClose={closeModal}
          loading={modal === 'create' ? creating : updating}
        />
      )}
      {modal === 'delete' && selected && (
        <DeleteModal
          staff={selected}
          onConfirm={handleDelete}
          onClose={closeModal}
          loading={deleting}
        />
      )}
      {modal === 'generate-login-id' && selected && (
        <GenerateLoginIdModal
          staff={selected}
          onConfirm={handleGenerateLoginId}
          onClose={closeModal}
          loading={generating}
        />
      )}
      {modal === 'reset-password' && selected && (
        <ResetPasswordModal
          staff={selected}
          onConfirm={handleResetPassword}
          onClose={closeModal}
          loading={resetting}
        />
      )}
      {createdCredentials && (
        <CredentialsDialog
          loginId={createdCredentials.loginId}
          password={createdCredentials.password}
          onClose={() => setCreatedCredentials(null)}
        />
      )}
    </div>
  );
}
